import React from 'react';
import { ToggleWrapper } from './ToggleItem.styles';
import type { TextV2Props } from '../../../TextV2';
import { TextV2 } from '../../../TextV2';
import { pxToRem } from '@withjoy/joykit/theme';
import classNames from 'classnames';
import type { SegmentedControllerVariant } from '../../SegmentedController.types';

export type ToggleItemProps = Readonly<{
  label: string;
  value: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isActive?: boolean;
  isDisabled?: boolean;
  isMobile?: boolean;
  textProps?: TextV2Props;
  variant?: SegmentedControllerVariant;
}>;

const UNDERLINE_TEXT_PROPS: TextV2Props = {
  typographyVariant: 'button2',
  fontSize: pxToRem(15),
};

const PILL_TEXT_PROPS: TextV2Props = {
  typographyVariant: 'body1',
};

/**
 * A real `<button>`, not the `TextV2 as="a"` this used to render. That anchor carried no `href` and
 * no `tabIndex`, so no segmented controller in the app was reachable by keyboard -- an operability
 * failure (WCAG 2.1.1), not a styling one.
 *
 * `aria-pressed` rather than tab semantics: the control is used both as a tab strip and as a filter
 * row, and `role="tablist"` would oblige every consumer to also own arrow-key roving focus and
 * panel wiring. A group of toggle buttons is honest about what this actually is.
 *
 * `data-toggle-item` is the sizing hook for consumers that need a taller item than the shared 36px.
 * Two of them reached in as `& a`, and the anchor -> button change silently broke both: the
 * selector simply stopped matching and the control fell back to the default height, with nothing
 * to fail. Target the attribute rather than the tag so the element stays an implementation detail.
 */
const ToggleItem: React.FC<ToggleItemProps> = ({ onClick, isActive, value, isMobile, textProps = {}, isDisabled, variant }) => {
  const isUnderline = variant === 'underline';
  const baseTextProps = isUnderline ? UNDERLINE_TEXT_PROPS : PILL_TEXT_PROPS;
  const inactiveColor = isUnderline ? 'mono8' : 'mono10';

  return (
    <ToggleWrapper
      as="button"
      type="button"
      aria-pressed={isActive}
      disabled={isDisabled}
      data-toggle-item=""
      isMobile={isMobile}
      variant={variant}
      onClick={isDisabled ? undefined : onClick}
      className={classNames({ active: isActive, disabled: isDisabled })}
    >
      <TextV2
        color={isActive ? 'mono14' : inactiveColor}
        {...baseTextProps}
        {...textProps}
      >
        {value}
      </TextV2>
    </ToggleWrapper>
  );
};

export { ToggleItem };
